import React from 'react';
import { useAuth } from '../../context/AuthContext';

interface CreditCostPreviewProps {
    creditCost: number | null;
    isEstimating: boolean;
    error: string | null;
    canAfford: boolean;
    className?: string;
}

const CreditCostPreview: React.FC<CreditCostPreviewProps> = ({
    creditCost,
    isEstimating,
    error,
    canAfford,
    className = ''
}) => {
    const { userCredits } = useAuth();

    // Don't render anything if we haven't estimated yet and there's no error
    if (creditCost === null && !isEstimating && !error) {
        return null;
    }

    // Error state
    if (error) {
        return (
            <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-3 ${className}`}>
                <div className="flex items-center">
                    <i className="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
                    <div>
                        <p className="text-sm text-yellow-700">
                            Unable to estimate cost: {error}
                        </p>
                        <p className="text-xs text-yellow-600 mt-1">
                            Generation will still work normally
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state
    if (isEstimating) {
        return (
            <div className={`bg-blue-50 border border-blue-200 rounded-lg p-3 ${className}`}>
                <div className="flex items-center">
                    <div className="inline-block w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mr-2"></div>
                    <p className="text-sm text-blue-700">Calculating credit cost...</p>
                </div>
            </div>
        );
    }

    // Credit cost display
    if (creditCost !== null) {
        return (
            <div className={`border rounded-lg p-3 ${canAfford
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
                } ${className}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <i className={`fas fa-coins mr-2 ${canAfford ? 'text-green-600' : 'text-red-600'
                            }`}></i>
                        <div>
                            <p className={`text-sm font-medium ${canAfford ? 'text-green-800' : 'text-red-800'
                                }`}>
                                This will cost {Math.abs(creditCost).toFixed(2)} credit{Math.abs(creditCost) !== 1 ? 's' : ''}
                            </p>
                            <p className={`text-xs ${canAfford ? 'text-green-600' : 'text-red-600'
                                }`}>
                                You have {userCredits.toFixed(2)} credit{userCredits !== 1 ? 's' : ''}
                                {!canAfford && (
                                    <span className="font-medium ml-1">
                                        (Need {Math.abs(creditCost - userCredits).toFixed(2)} more)
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>

                    {!canAfford && (
                        <div className="flex items-center">
                            <i className="fas fa-exclamation-circle text-red-500"></i>
                        </div>
                    )}
                </div>

                {!canAfford && (
                    <div className="mt-2 pt-2 border-t border-red-200">
                        <p className="text-xs text-red-600">
                            <i className="fas fa-info-circle mr-1"></i>
                            Please upgrade your plan or purchase more credits to continue
                        </p>
                    </div>
                )}
            </div>
        );
    }

    return null;
};

export default CreditCostPreview; 